import { useMemo, useRef } from "react"
import { Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useSave } from "lib/@hooks/use-save"
import { getTreeId } from "routes/schedule/lib/getTreeId"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { isEnabled } from "lib/@components/feature"
import { getSharingLink } from "routes/sharing/controller/get-sharing-link"
import { getShareLinkFieldResponseForViewer, saveShareLinkFieldResponse } from "./share-link-fields.controller"
import { RequestedFieldInput } from "./requested-field-input"
import { FEATURE_FLAG_SHARE_LINK_FIELDS } from "./feature-flag-share-link-fields"

const EMPTY_FIELDS = []

/**
 * Recipient-facing "Requested information" panel. Shown as a right-bar tab when the recipient is
 * viewing a share link whose creator requested one or more fields (see B2 builder). It loads the
 * viewer's own previously-saved response, lets them fill in each field, and auto-saves the whole
 * value map on every change via the standard `useSave` -> controller chain.
 *
 * `regimeId` and `shareLinkId` are resolved exactly the way the share-link comments path resolves
 * them: `shareLinkId` from the `share` search param, `regimeId` from `getTreeId(schedulePath)` on the
 * bound context. This keeps the recipient's responses keyed to the same regime the comments use.
 *
 * On the recipient landing there is no `schedulePath`, so the landing passes the regime id it already
 * knows (from the route) via the optional `regimeId` prop; it takes precedence over the bound
 * `getTreeId(schedulePath)` fallback used when this panel is hosted from a schedule context.
 */
export function RequestedFieldsPanel({ regimeId: regimeIdProp } = {}) {
    const { schedulePath } = useBoundContext()
    const [shareLinkId] = useSearchParam("share", null)
    const regimeId = regimeIdProp ?? getTreeId(schedulePath)
    const shareLink = getSharingLink.useResults(shareLinkId)
    const requestedFields = shareLink?.requestedFields ?? EMPTY_FIELDS

    const emptyResponse = useRef(null)
    const existing =
        getShareLinkFieldResponseForViewer.useResultsOnce({ shareLinkId, regimeId }, withInitialValue(IS_LOADING)) ??
        emptyResponse.current

    const target = useEditingTarget(existing)
    const save = useSave({ save: persist, target, wait: 1000 })

    // Require the share-link-fields feature demand alongside the link's saved requestedFields.
    // Recipients inherit the demand from the link owner's client auth bundle (ConfigureShareLinkDemands),
    // so when the feature/auth bundle is disabled the demand is absent and the requested-information form
    // closes rather than bleeding through the link's persisted requestedFields (FIQ-1668). The server
    // re-checks on read/save.
    if (!shareLinkId || !requestedFields.length || !isEnabled(FEATURE_FLAG_SHARE_LINK_FIELDS)) return null
    if (existing === IS_LOADING) return <LoadingScreen />

    return (
        <Stack spacing={1.5} data-cy="share-link-requested-fields-panel" sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="subtitle1">Requested information</Typography>
            <Typography variant="body2" color="text.secondary">
                Please complete the information requested below. Your answers save automatically.
            </Typography>
            <Bound target={target} onChange={save}>
                <Stack spacing={2}>
                    <Repeat list={requestedFields} pass="field" keyFn={fieldKey} item={<RequestedFieldInput />} />
                </Stack>
            </Bound>
        </Stack>
    )

    async function persist(current) {
        try {
            await saveShareLinkFieldResponse({ shareLinkId, regimeId, values: current.values })
        } catch (e) {
            errorSnackbar("Couldn't save your information")
        }
    }

    function fieldKey(field, index) {
        return field?.id ?? index
    }
}

// Build the editable target once per loaded response. The recipient edits `target.values` in place via
// the bound inputs, so the object identity must stay stable across re-renders (otherwise typing would
// reset). We re-seed only when a different stored response arrives (e.g. first load resolving).
function useEditingTarget(existing) {
    const responseId = existing && existing !== IS_LOADING ? existing._id ?? null : null
    return useMemo(
        () => ({ values: { ...(existing && existing !== IS_LOADING ? existing.values : null) } }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [responseId]
    )
}
